import { Modal, Form, Select, Typography } from 'antd';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { doctorApi } from '../../../api/doctor.api';
import { showConfirm } from '../../../components/common/ConfirmModal';

const AssignDoctorModal = ({ open, onClose, onSubmit, record, loading }) => {
  const [form] = Form.useForm();
  const clinicId = record?.targetClinic?.id || record?.targetClinic?._id || record?.targetClinic;

  const { data: doctorsData, isLoading: doctorsLoading } = useQuery({
    queryKey: ['doctors-active', clinicId],
    queryFn: () => doctorApi.getActive({ clinic: clinicId }),
    enabled: open && !!clinicId,
  });

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const currentAssigned = record?.assignedDoctor?.id || record?.assignedDoctor?._id;
    if (currentAssigned) { form.setFieldsValue({ doctorId: currentAssigned }); }
  }, [open, record, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      const doctor = (doctorsData?.data || []).find((d) => (d.id || d._id) === values.doctorId);
      const doctorName = doctor?.name || 'the selected doctor';
      const currentAssignedId = record?.assignedDoctor?.id || record?.assignedDoctor?._id;
      const isReassign = !!currentAssignedId && currentAssignedId !== values.doctorId;
      const isRejectedApproval = record?.status === 'rejected';

      showConfirm({
        title: isReassign ? 'Confirm Reassignment' : isRejectedApproval ? 'Confirm Approval' : 'Confirm Assignment',
        content: isReassign
          ? `Reassign ${record?.childName}'s referral from Dr. ${record?.assignedDoctor?.name} to Dr. ${doctorName}?`
          : isRejectedApproval
            ? `Approve this previously rejected referral and assign Dr. ${doctorName} to ${record?.childName}?`
            : `Assign ${record?.childName}'s referral to Dr. ${doctorName}?`,
        okText: isReassign ? 'Reassign' : 'Confirm',
        onOk: () => onSubmit(values.doctorId),
      });
    });
  };

  return (
    <Modal
      title="Assign Referral to Doctor"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={loading}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 16 }}>
        Choose a doctor at <strong>{record?.targetClinic?.name || 'the target clinic'}</strong> to
        handle <strong>{record?.childName}</strong>&apos;s referral. The doctor will be notified by email.
      </Typography.Paragraph>
      <Form form={form} layout="vertical">
        <Form.Item name="doctorId" label="Doctor" rules={[{ required: true, message: 'Please select a doctor' }]}>
          <Select
            placeholder="Select doctor"
            showSearch
            optionFilterProp="children"
            loading={doctorsLoading}
            notFoundContent={clinicId ? 'No active doctors at this clinic' : 'No clinic on this referral'}
          >
            {(doctorsData?.data || []).map((d) => (
              <Select.Option key={d.id || d._id} value={d.id || d._id}>{d.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AssignDoctorModal;
